import React, { useEffect, useState } from "react";
import { Container, Alert, ConnectedAlert, ProgressBar } from "../../../components";
import { Text, Heading, Button } from "@telia/teddy";
import Cookies from "js-cookie";
import { connect } from "react-redux";
import { selectors as AUTH_SELECTORS } from "../..";
import { RootState } from "../../../../../models";
import {
    getErrorMessage,
    getFlashMessage,
    isOrderExpired as isOrderExpiredSelector,
    isOrderNotFound as isOrderNotFoundSelector,
} from "../../../app/state/selectors";
import BankIdSection from "../../../checkout/components/molecules/BankIdSectionTeliaBroker/BankIdSectionTeliaBroker";
import { selectors as LOADING_SELECTORS } from "../../../loading";
import TEXT from "../../text-consts";
import "./BankIdAuthentication.less";

interface StateProps {
    customer?: string;
    errorMessage?: string;
    isBankIdPerformed: boolean;
    isBankIdVerified: boolean;
    isErrorDoingAuthentication: boolean;
    isFetchingData: boolean;
    isOrderExpired: boolean;
    isOrderNotFound: boolean;
    shouldShowBankId: boolean;
}

type BankIdAuthenticationProps = StateProps;

const AUTH_COOKIE_NAME = "orderRef.authenticateUser";
const AUTH_LANGUAGE_STORAGE_KEY = "auth-page-language";

const getInitialLanguage = (): "no" | "en" => {
    try {
        return window.sessionStorage.getItem(AUTH_LANGUAGE_STORAGE_KEY) === "en" ? "en" : "no";
    } catch {
        return "no";
    }
};

const getAuthenticationCookieName = () => {
    const cookieNames = Object.keys(Cookies.get());

    return cookieNames.find(
        (cookieName) =>
            (cookieName === AUTH_COOKIE_NAME || cookieName.startsWith(`${AUTH_COOKIE_NAME}.`)) &&
            !cookieName.endsWith(".completed"),
    );
};

const getAuthenticationCookieOptions = () => {
    const hostname = window.location.hostname;
    const isTeliaDomain = hostname === "telia.no" || hostname.endsWith(".telia.no");

    return isTeliaDomain ? { path: "/", domain: ".telia.no" } : { path: "/" };
};

const BankIdAuthentication: React.FC<BankIdAuthenticationProps> = ({
    // Redux (data)
    isBankIdVerified,
    isBankIdPerformed,
    isFetchingData,
    isOrderExpired,
    isOrderNotFound,
    customer,
    shouldShowBankId,
    errorMessage,
}) => {
    const showBankId = !isOrderNotFound && !isOrderExpired && !isBankIdVerified && !isBankIdPerformed;
    const isCustomerAuthenticated = !shouldShowBankId && customer;

    useEffect(() => {
        if (isBankIdVerified && isCustomerAuthenticated) {
            const cookieName = getAuthenticationCookieName();

            if (!cookieName) {
                return;
            }

            const cookieValue = Cookies.get(cookieName);
            const cookieOptions = getAuthenticationCookieOptions();

            // Clear the active session cookie and replace with a completed marker
            Cookies.remove(cookieName, cookieOptions);
            Cookies.remove(cookieName, { path: "/" });

            if (!cookieValue) {
                return;
            }

            Cookies.set(`${cookieName}.completed`, cookieValue, {
                ...cookieOptions,
                expires: 30 / (24 * 60), // 30 min
            });
        }
    }, [isBankIdVerified, isCustomerAuthenticated]);
    const [language, setLanguage] = useState<"no" | "en">(getInitialLanguage);
    const isEnglish = language === "en";
    const authText = TEXT.AUTH_PAGE;

    const toggleLanguage = () => {
        const nextLanguage = isEnglish ? "no" : "en";
        setLanguage(nextLanguage);

        try {
            window.sessionStorage.setItem(AUTH_LANGUAGE_STORAGE_KEY, nextLanguage);
        } catch {
            // Ignore unavailable browser storage
        }
    };

    return (
        <div className="bankid-auth-page" lang={isEnglish ? "en" : "nb"}>
            <div className="bankid-auth-page__heading-container">
                <Heading mb="0" as="h1" variant="title-600">
                    {isEnglish ? authText.EN.HEADING : authText.HEADING}
                </Heading>
            </div>
            <ProgressBar isFetchingData={isFetchingData} standByColor="light-grey" />
            <ConnectedAlert />
            <Container size="small">
                {isOrderExpired && (
                    <Text mb="100" variant="preamble-200">
                        {isEnglish ? authText.EN.ORDER_EXPIRED : authText.ORDER_EXPIRED}
                    </Text>
                )}
                {isOrderNotFound && (
                    <Text mb="100" variant="preamble-200">
                        {isEnglish ? authText.EN.ORDER_NOT_FOUND : authText.ORDER_NOT_FOUND}
                    </Text>
                )}
                {errorMessage && <Alert kind="warning">{errorMessage}</Alert>}
                {showBankId && (
                    <Container className="install-esim__bankid-container">
                        <BankIdSection referer="auth-user" skipDisplayHeading={true} language={language} />
                    </Container>
                )}
                <div className="bankid-auth-page__language-toggle">
                    <Button variant="secondary" size="sm" lang={isEnglish ? "nb" : "en"} onClick={toggleLanguage}>
                        {isEnglish ? authText.EN.LANGUAGE_TOGGLE : authText.LANGUAGE_TOGGLE_NO}
                    </Button>
                </div>
                {isBankIdVerified && isCustomerAuthenticated && (
                    <Text mb="100" variant="preamble-200" data-di-mask>
                        {isEnglish ? authText.EN.RESULT_TEXT(customer) : authText.RESULT_TEXT(customer)}
                    </Text>
                )}
            </Container>
        </div>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    isBankIdVerified: AUTH_SELECTORS.isBankIdVerified(state),
    isBankIdPerformed: AUTH_SELECTORS.isBankIdPerformed(state),
    isErrorDoingAuthentication: !!getFlashMessage(state),
    isFetchingData: LOADING_SELECTORS.isLoading(state),
    isOrderExpired: isOrderExpiredSelector(state),
    isOrderNotFound: isOrderNotFoundSelector(state),
    customer: AUTH_SELECTORS.getCustomer(state),
    shouldShowBankId: AUTH_SELECTORS.shouldShowBankId(state),
    errorMessage: getErrorMessage(state),
});

export default connect(mapStateToProps)(BankIdAuthentication);
